import { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '../../api';
import { useApp } from '../../contexts/AppContext';
import { usePolling } from '../../hooks/usePolling';
import { InventoryProvider } from '../../contexts/InventoryContext';
import { DocumentInventory } from '../../components/inventory';
import DocumentViewer from '../../components/DocumentViewer';
import BackgroundJobsBanner from '../../components/BackgroundJobsBanner';
import PendingUpdatesCallout from '../../components/inventory/PendingUpdatesCallout';
import InventoryModeBanner from '../../components/inventory/InventoryModeBanner';
import useInventoryMode from '../../hooks/useInventoryMode';
import { findActiveMode } from '../../contexts/inventoryModes';
import { useNotepad } from '../../contexts/NotepadContext';
import { useAuth } from '../../contexts/AuthContext';
import { buildNotesIndex, setNotesIndex } from '../../contexts/notesIndex';

export default function InventoryTab({ caseId }) {
  const { documents, folderTree, caseData, caseDashboard, dispatch } = useApp();
  const navigate = useNavigate();

  // Publish per-document notepad clip counts so the inventory's notes column
  // and "Notes" filter axis can read them (notesIndex is a hook-less leaf).
  const notepad = useNotepad();
  const { user } = useAuth();
  const notesClips = notepad?.clips;
  useMemo(
    () => setNotesIndex(buildNotesIndex(notesClips || [], user?.uid)),
    [notesClips, user?.uid]
  );
  const [viewerDocId, setViewerDocId] = useState(null);
  const [viewerPage, setViewerPage] = useState(null);      // 1-based page to open at (detected documents)
  const [viewerStartAt, setViewerStartAt] = useState(null);
  const [checklistDocMap, setChecklistDocMap] = useState({});

  // Load checklist-to-document mapping for badges
  const refetchChecklistDocMap = useCallback(async () => {
    try {
      const data = await api.get(`/cases/${caseId}/checklist/doc-map`);
      setChecklistDocMap(data.doc_map || {});
    } catch { /* ignore — no checklist loaded */ }
  }, [caseId]);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const data = await api.get(`/cases/${caseId}/checklist/doc-map`);
        if (!cancelled) setChecklistDocMap(data.doc_map || {});
      } catch { /* ignore — no checklist loaded */ }
    }
    load();
    return () => { cancelled = true; };
  }, [caseId]);

  // Refresh folder tree
  const refreshTree = useCallback(async () => {
    try {
      const data = await api.get(`/documents/case/${caseId}/folder-tree`);
      dispatch({ type: 'SET_FOLDER_TREE', tree: data });
    } catch { /* ignore */ }
  }, [caseId, dispatch]);

  // Delta-fetch cursor — server's clock at the last successful response.
  // Sent as ?since= on the next poll so the backend can return only
  // documents whose own row OR whose related processing tasks changed
  // since then. Initialized from the dashboard envelope when present so
  // the first poll already runs as a delta. Reset to null on full
  // refreshes (folder operations etc.) to force a full list re-fetch.
  const lastDocsServerTimeRef = useRef(
    caseDashboard?.documents?.server_time || null,
  );

  // Refresh docs — delta when a server_time cursor is available, full
  // refresh otherwise. Both branches keep the SPA in sync with backend
  // status; delta is just a smaller wire payload (~5 KB vs ~3.74MB on
  // Brenner). Pool Governance follow-on, 2026-05-28.
  const refreshDocs = useCallback(async (forceFull = false) => {
    try {
      const since = forceFull ? null : lastDocsServerTimeRef.current;
      const path = since
        ? `/documents/case/${caseId}?since=${encodeURIComponent(since)}`
        : `/documents/case/${caseId}`;
      const data = await api.get(path);
      // server_time is the server's clock at THIS response — what we
      // send as the next ?since. If the response shape is the legacy
      // bare list (no envelope), fall back to client time so we still
      // advance the cursor instead of looping at the same since.
      const nextCursor = data?.server_time || new Date().toISOString();
      lastDocsServerTimeRef.current = nextCursor;
      if (since && data?.since) {
        // Delta payload — merge.
        dispatch({ type: 'MERGE_DOCUMENTS', docs: data.documents || [] });
      } else {
        // Full refresh — replace.
        dispatch({ type: 'SET_DOCUMENTS', docs: data.documents || data });
      }
    } catch { /* ignore */ }
  }, [caseId, dispatch]);

  // Full refresh after mutating ops (folder rename/move/delete). Resets
  // the delta cursor so the next list is authoritative — covers
  // deletions, which a delta endpoint can't express by design.
  const refreshDocsFull = useCallback(() => {
    lastDocsServerTimeRef.current = null;
    return refreshDocs(true);
  }, [refreshDocs]);

  // Refresh case metadata (e.g. last_propagation_run_at after a propagation run)
  const refreshCase = useCallback(async () => {
    try {
      const data = await api.get(`/cases/${caseId}`);
      dispatch({ type: 'UPDATE_CASE_DATA', data });
    } catch { /* ignore */ }
  }, [caseId, dispatch]);

  // Combined refresh — docs (delta) + folder tree in parallel.
  // Tree counts drift behind during active uploads because the tree endpoint
  // isn't on any other refresh path; polling it alongside docs keeps the
  // sidebar "All Files" / folder counts consistent with the stats bar.
  const refreshAll = useCallback(async () => {
    await Promise.all([refreshDocs(), refreshTree()]);
  }, [refreshDocs, refreshTree]);

  // Mutations (move/delete/rename) trigger a FULL refresh — deltas
  // can't express deletions, and the user just took an intentional
  // structural action, so we want a clean authoritative state.
  const handleDocChange = useCallback(async () => {
    await Promise.all([refreshDocsFull(), refreshTree()]);
  }, [refreshDocsFull, refreshTree]);

  // Poll while documents are processing/pending so status badges update live.
  const hasProcessing = useMemo(() =>
    (documents || []).some(d => d.status === 'processing' || d.status === 'pending'),
    [documents],
  );
  // Fast poll (8s) while processing, slow background poll (30s) otherwise.
  // Skip the immediate refetch when the case-page mount dashboard already
  // seeded `documents` + `folderTree` — the poll cadence picks up
  // freshness without a duplicate fan-out on mount (Pool Governance P2).
  const seeded = caseDashboard?.documents != null && caseDashboard?.folder_tree != null;
  usePolling(refreshAll, hasProcessing ? 8000 : 30000, true, !seeded);

  // Refetch when the tab comes back into focus — covers the "left the page
  // open overnight" case cheaply without pushing the polling cadence higher.
  useEffect(() => {
    const onVisible = () => {
      if (!document.hidden) refreshAll();
    };
    window.addEventListener('focus', refreshAll);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.removeEventListener('focus', refreshAll);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [refreshAll]);

  // Chat scope handler
  const handleChatScope = useCallback((docIds) => {
    dispatch({
      type: 'SET_CHAT_SCOPE',
      scope: {
        document_ids: docIds,
        label: `${docIds.length} selected document${docIds.length !== 1 ? 's' : ''}`,
      },
    });
    navigate(`/case/${caseId}/exploration`);
  }, [caseId, dispatch, navigate]);

  // Inventory Mode — purpose-driven engagement of the inventory tab.
  // First consumer (Phase 2): matchChecklistItem.
  const [searchParams] = useSearchParams();
  // Synchronous URL-only signal so DocumentInventory's mount hydration
  // can skip the last-saved-view filter fallback before resolveContext
  // (which is async) settles. Without this, a user entering match mode
  // gets trapped in their prior saved view's filter subset.
  const inMode = useMemo(() => findActiveMode(searchParams) != null, [searchParams]);
  const { mode, context: modeContext } = useInventoryMode({ caseId });
  const handleCancelMode = useCallback(() => {
    if (!mode) return;
    const path = mode.returnPath
      ? mode.returnPath({ context: modeContext, caseId })
      : `/case/${caseId}/inventory`;
    navigate(path);
  }, [mode, modeContext, caseId, navigate]);

  // Called by mode-aware bulk actions / viewer buttons after they write
  // a manual match. Refreshes the checklist doc-map (so badges + dimming
  // reflect the new mapping) and navigates back to the originating tab.
  const handleMatchRecorded = useCallback(async () => {
    await refetchChecklistDocMap();
    if (mode?.returnPath) {
      navigate(mode.returnPath({ context: modeContext, caseId }));
    }
  }, [mode, modeContext, caseId, navigate, refetchChecklistDocMap]);

  const bulkActionContext = useMemo(
    () =>
      mode && modeContext
        ? { mode, modeContext, onMatchRecorded: handleMatchRecorded }
        : null,
    [mode, modeContext, handleMatchRecorded],
  );

  // Shared "Use this document as the match" write path. Called by:
  //   - per-row inline button on InventoryTable (when in match mode)
  //   - "Use for this item" button in the document viewer header
  // Same payload as the useAsChecklistMatch bulk action. Closes the
  // viewer + refreshes doc-map + navigates back to the checklist.
  const [matchBusy, setMatchBusy] = useState(false);
  const useDocAsMatch = useCallback(async (doc) => {
    if (!doc?.id || !modeContext?.itemId) return;
    setMatchBusy(true);
    try {
      await api.patch(`/cases/${caseId}/checklist/items/batch`, {
        updates: [{
          item_id: modeContext.itemId,
          status: 'matched',
          source_document_id: doc.id,
          file_path: doc.relative_path || doc.file_path || null,
          manually_verified: true,
          match_method: 'manual_pick',
        }],
      });
      setViewerDocId(null);
      setViewerStartAt(null);
      await handleMatchRecorded();
    } finally {
      setMatchBusy(false);
    }
  }, [caseId, modeContext, handleMatchRecorded]);

  const onUseAsMatch = mode?.key === 'matchChecklistItem' && modeContext ? useDocAsMatch : null;

  const renderViewerMatchAction = useCallback(({ document: doc }) => {
    if (!mode || mode.key !== 'matchChecklistItem' || !modeContext) return null;
    return (
      <button
        type="button"
        className="btn btn-sm btn-primary"
        disabled={matchBusy}
        onClick={() => useDocAsMatch(doc)}
      >
        📎 Use for this item
      </button>
    );
  }, [mode, modeContext, matchBusy, useDocAsMatch]);

  return (
    <div className="inventory-tab">
      <BackgroundJobsBanner caseId={caseId} />
      <PendingUpdatesCallout caseId={caseId} />
      <InventoryModeBanner mode={mode} context={modeContext} onCancel={handleCancelMode} />
      <InventoryProvider>
        <DocumentInventory
          caseId={caseId}
          documents={documents}
          folderTree={folderTree}
          lastPropagationRunAt={caseData?.last_propagation_run_at ?? null}
          onChatScope={handleChatScope}
          onShowDetail={(docId) => { setViewerDocId(docId); setViewerStartAt(null); setViewerPage(null); }}
          onShowViewer={(docId, startAt = null, page = null) => {
            setViewerDocId(docId);
            setViewerStartAt(startAt);
            setViewerPage(page);
          }}
          onTreeChange={handleDocChange}
          onPropagated={refreshCase}
          checklistDocMap={checklistDocMap}
          bulkActionContext={bulkActionContext}
          inMode={inMode}
          onUseAsMatch={onUseAsMatch}
          matchBusy={matchBusy}
        />
      </InventoryProvider>
      {viewerDocId && (
        <DocumentViewer
          documentId={viewerDocId}
          startAt={viewerStartAt}
          initialPage={viewerPage}
          onClose={() => { setViewerDocId(null); setViewerStartAt(null); setViewerPage(null); }}
          onOpenDocument={(id, opts = null) => {
            // opts.startAt: an excerpt opening its original at the IN point.
            const t = Number(opts?.startAt);
            setViewerStartAt(Number.isFinite(t) ? t : null);
            setViewerPage(null);
            setViewerDocId(id);
          }}
          headerActions={mode?.key === 'matchChecklistItem' ? renderViewerMatchAction : null}
        />
      )}
    </div>
  );
}
